import {
  ChangeDetectionStrategy,
  Component,
  computed,
  Input,
  OnChanges,
  input,
  signal,
  SimpleChanges,
} from '@angular/core';
import { Subject } from 'rxjs';
import { BdExportAsFileComponent } from '@bindoc-projects/export-as-file-lib/ui';
import {
  KostDataImportDatasetInputProgress,
  KostDataImportLogExport,
} from '@bindoc-projects/kost-data-import-domain-lib';
import { BdUiStepProgressComponent } from '@bindoc-projects/ui-lib/feedback/ui';
import {
  datasetSteps,
  KostDataImportDatasetStatus,
  KostDataImportDatasetStepState,
} from './dataset-steps';

function heading(status: KostDataImportDatasetStatus): string {
  switch (status) {
    case 'running':
      return 'Datei wird verarbeitet';
    case 'done':
      return 'Datei verarbeitet';
    case 'failed':
      return 'Verarbeitung abgebrochen';
    default:
      return '';
  }
}

const NO_STEP: KostDataImportDatasetStepState = { index: -1, percent: null };

/**
 * Processing of the selected file as `bd-ui-step-progress`: the processor's
 * steps with the running one's own percentage and latest log line. The full
 * log stays downloadable.
 */
@Component({
  selector: 'kost-data-import-new-dataset-input-result-log',
  template: `
    @if (status() !== 'idle') {
      <bd-ui-step-progress
        data-test="kost-dataset-progress"
        [heading]="heading()"
        [steps]="progressSteps()"
      >
        @if (log().length > 0) {
          <bd-export-as-file
            stepProgressAction
            [data]="exportData"
            [filename]="exportFileName"
          >
          </bd-export-as-file>
        }
      </bd-ui-step-progress>
      @if (status() === 'running') {
        <small class="mt-2 block text-xs text-muted-color" i18n
          >Dieser Vorgang kann mehrere Minuten in Anspruch nehmen.</small
        >
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BdExportAsFileComponent, BdUiStepProgressComponent],
})
export class KostDataImportNewDatasetInputResultLogComponent implements OnChanges {
  @Input() progress$: Subject<KostDataImportDatasetInputProgress>;

  /** The processor's steps in order, step id → label. */
  public readonly steps = input<Readonly<Record<string, string>>>({});
  public readonly status = input<KostDataImportDatasetStatus>('idle');

  public exportData: KostDataImportLogExport = new KostDataImportLogExport();
  public exportFileName = 'import-log';

  // Signal-backed state so progress events emitted from the worker (which runs
  // outside Angular's zone and won't have any zone-driven CD at all once the
  // app goes zoneless) still flush through the reactive graph and re-render
  // the log.
  public readonly log = signal<string[]>([]);
  public readonly step = signal<KostDataImportDatasetStepState>(NO_STEP);

  protected readonly heading = computed(() => heading(this.status()));

  protected readonly progressSteps = computed(() => {
    const status = this.status();
    return status === 'idle'
      ? []
      : datasetSteps(Object.values(this.steps()), this.step(), status);
  });

  ngOnChanges(changes: SimpleChanges) {
    if (changes['progress$']) {
      this.log.set([]);
      this.step.set(NO_STEP);
      this.progress$.subscribe((event) => this._onProgress(event));
    }
  }

  private _onProgress(event: KostDataImportDatasetInputProgress) {
    const index = Object.keys(this.steps()).indexOf(event.currentStep);

    if (index >= 0) {
      // A step id: the processor moved on, or reports progress of its step.
      this.step.update((prev) =>
        index < prev.index
          ? prev
          : {
              index,
              percent: event.stepPercent ?? null,
              detail: index === prev.index ? prev.detail : undefined,
            },
      );
      return;
    }

    if (event.type === 'STEP_UPDATE') {
      this.log.update((prev) => [...prev, event.currentStep]);
      this.exportData.rows = this.log();
    }
    if (event.type) {
      this.step.update((prev) => ({ ...prev, detail: event.currentStep }));
    }
  }
}
